/**
 * Axios adapter for standalone DEMO mode (REACT_APP_DEMO=true).
 *
 * Every request made through `src/api/clients.js` is answered locally, so the
 * whole re-enrolment flow can be demonstrated with no backend and no real
 * credentials. Responses mirror the shapes the current (development) frontend
 * consumes — in particular `/parent/get-parent-history` returns the nested
 * `parent.parent_codes[].students[]` structure that
 * `src/utils/parentHistoryHelpers.js` reads.
 *
 * Log in with any Parent ID / password; `/parent-login` always succeeds.
 */

import { getCampusPaymentCharges, getCampusSessions } from '../utils/campusPaymentCharges.js';
import {
    CAMPUS_PAYMENT_FULL,
    CAMPUS_PAYMENT_MINIMUM,
    DISCOUNT_EBD,
    DISCOUNT_HCC,
    DISCOUNT_STAFF,
    defaultCampusPaymentMode,
    discountForCampusPayment,
    findCampusPaymentSelection,
    isMultiCampusPayment,
    usesPerCampusPaymentOptions,
} from '../utils/campusPaymentOptions.js';

const NEXT_YEAR = new Date().getFullYear() + 1;

/**
 * Demo campuses mirror the two parent codes in `demoParent`, so a demo family is
 * always multi-campus and the one-campus-at-a-time payment loop is exercised.
 */
const DEMO_CAMPUSES = [
    { SessionID: 'DEMO_SESSION_1', college: 'Kewdale', parent_code: 'P1001' },
    { SessionID: 'DEMO_SESSION_2', college: 'Thornlie', parent_code: 'P2001' },
];

/**
 * Demo mode has no server to remember payments, so the campuses the client has
 * already marked paid stand in for backend state. This is what lets a demo run
 * pay one campus, land back on the fee page, and resume after a fresh login.
 */
const demoCampusStatuses = () => {
    const charges = getCampusPaymentCharges();
    const known = charges.length ? charges : getCampusSessions();
    const rows = known.length ? known : DEMO_CAMPUSES;
    const paidIds = new Set(
        charges.filter((row) => row.paymentStatus === 'paid').map((row) => String(row.SessionID))
    );

    return rows.map((row, index) => {
        const paid = paidIds.has(String(row.SessionID));
        return {
            SessionID: row.SessionID ?? DEMO_CAMPUSES[index]?.SessionID,
            college: row.college ?? DEMO_CAMPUSES[index]?.college,
            parent_code: row.parent_code ?? DEMO_CAMPUSES[index]?.parent_code,
            paid,
            status: paid ? 'Completed' : 'Initiated',
        };
    });
};

const demoFamilyPaymentState = () => {
    const campusStatuses = demoCampusStatuses();
    const paidCampuses = campusStatuses.filter((campus) => campus.paid);
    const allComplete = campusStatuses.length > 0 && paidCampuses.length === campusStatuses.length;

    return {
        campusStatuses,
        paidCampuses,
        allComplete,
        partialPayment: paidCampuses.length > 0 && !allComplete,
    };
};

const demoContact = {
    email1: 'demo.parent@example.com',
    email2: 'demo.spouse@example.com',
    mobile1: '(M) 0412 345 678',
    mobile2: '(F) 0498 765 432',
};

const demoAddress = {
    address_line1: '123 Islamic Academy Way',
    address_line2: 'Building 4',
    address_line3: '',
    town_suburb: 'Perth',
    postcode: '6000',
    state: 'WA',
};

const medical = (overrides = {}) => ({
    medicare_number: '1234567890',
    medicare_ref_no: '1',
    medicare_expiry: `12/${NEXT_YEAR + 2}`,
    blood_group: 'A+',
    allergy: 'N',
    ambulance_cover: 'YES',
    immunisation_status: 'YES',
    private_health_insurance: 'Y',
    ...overrides,
});

/** Nested parent-history payload — the shape parentHistoryHelpers expects. */
const demoParent = {
    parent_code: 'P1001',
    family_name: 'Khan',
    surname: 'Khan',
    contact: demoContact,
    address: demoAddress,
    parent_codes: [
        {
            parent_code: 'P1001',
            campus_code: '01',
            campus_name: 'Kewdale',
            // Negative balance is a campus credit (TASS bal_fwd convention).
            wallet_balance: -150,
            contact: demoContact,
            address: demoAddress,
            students: [
                {
                    student_code: 'STU001',
                    student_name: 'Ahmad',
                    student_surname: 'Khan',
                    year: 4,
                    section: 'Primary',
                    campus_code: '01',
                    campus_name: 'Kewdale',
                    parent_code: 'P1001',
                    imageUrl: '',
                    student_medical_data: medical({ medicare_ref_no: '1' }),
                },
                {
                    student_code: 'STU002',
                    student_name: 'Fatima',
                    student_surname: 'Khan',
                    year: 7,
                    section: 'Secondary',
                    campus_code: '01',
                    campus_name: 'Kewdale',
                    parent_code: 'P1001',
                    imageUrl: '',
                    student_medical_data: medical({ medicare_ref_no: '2', blood_group: 'O+', allergy: 'Y' }),
                },
            ],
        },
        {
            parent_code: 'P2001',
            campus_code: '02',
            campus_name: 'Thornlie',
            // Positive is owed. One campus in credit and the other in debt is the case this
            // fixture exists to show: neither figure reaches the other campus.
            wallet_balance: 200,
            contact: demoContact,
            address: demoAddress,
            students: [
                {
                    student_code: 'STU003',
                    student_name: 'Yusuf',
                    student_surname: 'Khan',
                    year: 10,
                    section: 'Secondary',
                    campus_code: '02',
                    campus_name: 'Thornlie',
                    parent_code: 'P2001',
                    imageUrl: '',
                    student_medical_data: medical({ medicare_ref_no: '3', blood_group: 'B+' }),
                },
            ],
        },
    ],
};

/** Per-year-group fee table used by both /getschoolfees and /calculatefees. */
const FEE_TABLE = {
    5: { deposit_fee: 100, resource_fees: 300, school_fees_wo_any_disc: 4200 },
    8: { deposit_fee: 100, resource_fees: 380, school_fees_wo_any_disc: 5100 },
    11: { deposit_fee: 100, resource_fees: 450, school_fees_wo_any_disc: 6300 },
};
const DEFAULT_FEE = { deposit_fee: 100, resource_fees: 350, school_fees_wo_any_disc: 4800 };

const feesForYear = (year) => FEE_TABLE[year] || DEFAULT_FEE;

/**
 * Family discount by total enrolled children (all campuses), matching feetbl.
 * 1 child: $0; 2: $500; 3: $900; 4+: $1200 — every eligible child gets that rate.
 */
const SIBLING_DISCOUNT_BY_FAMILY_SIZE = { 1: 0, 2: 500, 3: 900, 4: 1200, 5: 1200 };
const siblingDiscountForFamilySize = (count) => {
    const n = Math.min(Math.max(Number(count) || 1, 1), 5);
    return SIBLING_DISCOUNT_BY_FAMILY_SIZE[n] ?? 0;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const buildSchoolFeeRows = () =>
    [5, 8, 11].map((year) => {
        const base = feesForYear(year);
        return [
            {
                Year: year,
                Fees: [
                    {
                        ...base,
                        hcc_discount: 0,
                        sibling_discount: 0,
                        school_fees: base.school_fees_wo_any_disc,
                        school_fees_after_hcc_discount: base.school_fees_wo_any_disc,
                    },
                ],
            },
        ];
    });

/**
 * Mirrors the backend /calculatefees contract consumed by Payment /
 * PaymentPageTemplate: { totals, payment, validation, students,
 * schoolFeeRows, campusBreakdown }.
 *
 * Multi-campus families send campusPaymentSelections so each campus can be
 * priced as the re-enrolment minimum (HCC 5%) or the full amount (EBD or HCC).
 */
const calculateFees = (body = {}) => {
    const children = body.enrolledchildrens || body.childrens || [];
    const familyDiscountOption = body.DiscountOption || '';
    const staffDiscount = body.SD;
    const fte = Number(body.FTE) || 1;
    const creditAvailable = Math.abs(Number(body.credit_amount) || 0);
    // Per campus, from the same wallet that gives the credit: whichever a campus has, it has only
    // that one. Keyed by campus name, as the real endpoint keys it.
    const outstandingByCampus = {};
    demoParent.parent_codes.forEach((campus) => {
        const wallet = Number(campus.wallet_balance) || 0;
        outstandingByCampus[campus.campus_name] = wallet > 0 ? wallet : 0;
    });
    const giveHccDiscount = body.GiveHCCDiscount === true
        || body.GiveHCCDiscount === 'Yes'
        || familyDiscountOption === DISCOUNT_HCC;
    const campusSessions = body.campusSessions || DEMO_CAMPUSES;
    const incomingSelections = Array.isArray(body.campusPaymentSelections)
        ? body.campusPaymentSelections
        : [];

    const eligible = children.filter((c) => Number(c.year_grp) < 13);
    const familyChildCount = Math.min(Math.max(eligible.length, 1), 5);
    const familySiblingDiscount = siblingDiscountForFamilySize(familyChildCount);
    const isMultiCampus = isMultiCampusPayment([], campusSessions, eligible);
    const isStaff = staffDiscount === 'Yes' || familyDiscountOption === DISCOUNT_STAFF;
    const usePerCampusPayment = usesPerCampusPaymentOptions({
        isMultiCampus,
        staffDiscount,
        familyDiscountOption,
    });

    const students = eligible.map((child) => {
        const year = Number(child.year_grp);
        const base = feesForYear(year);
        const sibling_discount = familySiblingDiscount;
        const afterSibling = base.school_fees_wo_any_disc - sibling_discount;
        const campusName = child.campus_name || 'Kewdale';
        const campusSession = campusSessions.find((row) =>
            String(row.college || '').toLowerCase() === String(campusName).toLowerCase()
        ) || {};
        const selection = findCampusPaymentSelection(incomingSelections, {
            college: campusName,
            SessionID: campusSession.SessionID,
        });
        const paymentMode = usePerCampusPayment
            ? defaultCampusPaymentMode({
                existingMode: selection?.paymentMode,
                familyDiscountOption,
            })
            : (familyDiscountOption === DISCOUNT_EBD || isStaff ? CAMPUS_PAYMENT_FULL : CAMPUS_PAYMENT_MINIMUM);

        const campusDiscount = isStaff
            ? DISCOUNT_STAFF
            : (usePerCampusPayment
                ? (selection?.DiscountOption || discountForCampusPayment({
                    paymentMode,
                    giveHccDiscount,
                    staffDiscount,
                    familyDiscountOption,
                }))
                : familyDiscountOption);

        let hccDiscount = 0;
        let discount = 0;
        if (campusDiscount === DISCOUNT_HCC && child.HCC) {
            hccDiscount = round2(afterSibling * 0.05);
        } else if (campusDiscount === DISCOUNT_EBD) {
            discount = round2(afterSibling * 0.05);
        } else if (campusDiscount === DISCOUNT_STAFF && isStaff) {
            const rate = [-1, 11, 12].includes(year) ? fte * 0.25 : fte * 0.5;
            discount = round2(base.school_fees_wo_any_disc * rate);
        }

        const tuitionAfterDiscount = campusDiscount === DISCOUNT_STAFF
            ? round2(base.school_fees_wo_any_disc - discount)
            : round2(afterSibling - hccDiscount - discount);

        return {
            stud_code: child.stud_code,
            stud_id: child.stud_code,
            campus_name: campusName,
            campus_code: child.campus_code || child.cmpy_code || '',
            year_grp: year,
            deposit_fee: base.deposit_fee,
            original_deposit_fee: base.deposit_fee,
            resource_fee: base.resource_fees,
            school_fees_wo_any_disc: base.school_fees_wo_any_disc,
            sibling_discount,
            school_fees_after_sibling_discount: afterSibling,
            HCCDiscount: hccDiscount,
            school_fees_after_hcc_discount: round2(afterSibling - hccDiscount),
            discount,
            resource_fees_only: false,
            resource_only_discount: 0,
            paymentMode,
            DiscountOption: campusDiscount,
            minimumAmount: round2(base.deposit_fee + base.resource_fees),
            fullAmount: round2(tuitionAfterDiscount + base.resource_fees),
        };
    });

    const TotalSchoolFees = round2(
        students.reduce((sum, s) => sum + s.school_fees_wo_any_disc, 0)
    );
    const TotalDiscount = round2(
        students.reduce((sum, s) => sum + s.sibling_discount + s.HCCDiscount + s.discount, 0)
    );
    const TotalFeesWithDiscount = round2(TotalSchoolFees - TotalDiscount);
    const TotalResourceFees = round2(students.reduce((sum, s) => sum + s.resource_fee, 0));
    const TotalDepositFees = round2(students.reduce((sum, s) => sum + s.deposit_fee, 0));

    const campusBreakdown = Object.values(
        students.reduce((acc, s) => {
            const college = s.campus_name || 'Kewdale';
            acc[college] = acc[college] || {
                college,
                credit_available: 0,
                credit_applied: 0,
                AmountPaidFromBpoint: 0,
                TotalDepFees: 0,
                TotalResAmount: 0,
                TotalDiscount: 0,
                TotalSchoolFeeswodisc: 0,
                TotalFeesWithDiscount: 0,
                minimumAmount: 0,
                fullAmount: 0,
                paymentMode: s.paymentMode,
                DiscountOption: s.DiscountOption,
            };
            acc[college].TotalDepFees = round2(acc[college].TotalDepFees + s.deposit_fee);
            acc[college].TotalResAmount = round2(acc[college].TotalResAmount + s.resource_fee);
            acc[college].TotalDiscount = round2(
                acc[college].TotalDiscount + s.sibling_discount + s.HCCDiscount + s.discount
            );
            acc[college].TotalSchoolFeeswodisc = round2(
                acc[college].TotalSchoolFeeswodisc + s.school_fees_wo_any_disc
            );
            acc[college].minimumAmount = round2(acc[college].minimumAmount + s.minimumAmount);
            acc[college].fullAmount = round2(acc[college].fullAmount + s.fullAmount);
            acc[college].outstandingAmount = outstandingByCampus[college] || 0;
            acc[college].paymentMode = s.paymentMode;
            acc[college].DiscountOption = s.DiscountOption;
            return acc;
        }, {})
    );

    // Fold each campus's debt into what it asks for, exactly as the server does: into the minimum
    // as well as the full amount, so no route through the page leaves it behind.
    campusBreakdown.forEach((row) => {
        const owed = Number(row.outstandingAmount) || 0;
        row.feesMinimumAmount = row.minimumAmount;
        row.feesFullAmount = row.fullAmount;
        row.minimumAmount = round2(row.minimumAmount + owed);
        row.fullAmount = round2(row.fullAmount + owed);
    });

    campusBreakdown.forEach((row, index) => {
        const matchedSession = campusSessions.find((session) =>
            String(session.college || '').toLowerCase() === String(row.college).toLowerCase()
        ) || DEMO_CAMPUSES.find((session) => session.college === row.college);
        row.SessionID = matchedSession?.SessionID || DEMO_CAMPUSES[index]?.SessionID || `DEMO_SESSION_${index + 1}`;
        row.parent_code = matchedSession?.parent_code || DEMO_CAMPUSES[index]?.parent_code || 'P1001';
        const incoming = findCampusPaymentSelection(incomingSelections, row);
        row.paymentMode = defaultCampusPaymentMode({
            existingMode: incoming?.paymentMode || row.paymentMode,
            familyDiscountOption,
        });
        if (!usePerCampusPayment) {
            row.paymentMode = familyDiscountOption === DISCOUNT_EBD || isStaff
                ? CAMPUS_PAYMENT_FULL
                : CAMPUS_PAYMENT_MINIMUM;
        }
        row.DiscountOption = incoming?.DiscountOption
            || discountForCampusPayment({
                paymentMode: row.paymentMode,
                giveHccDiscount,
                staffDiscount,
                familyDiscountOption,
            })
            || row.DiscountOption;
        row.TotalFeesWithDiscount = round2(row.TotalSchoolFeeswodisc - row.TotalDiscount);
        const selectedAmount = row.paymentMode === CAMPUS_PAYMENT_FULL
            ? row.fullAmount
            : row.minimumAmount;
        // Single-campus families still pay deposit + resource unless EBD/full is chosen — plus
        // whatever that campus is owed for the current year, which is never optional.
        const defaultSingleCampus = round2(
            row.TotalDepFees + row.TotalResAmount + (Number(row.outstandingAmount) || 0)
        );
        row.payableAmount = usePerCampusPayment
            ? selectedAmount
            : (familyDiscountOption === DISCOUNT_EBD || isStaff ? row.fullAmount : defaultSingleCampus);
        row.PayableAmount = row.payableAmount;
        row.CustomAmount = row.payableAmount;
        row.RemainingAmount = round2(Math.max(row.fullAmount - row.payableAmount, 0));
        row.AmountPaidFromAICCredit = 0;
        row.AmountPaidFromBpoint = row.payableAmount;
    });

    const yearTotal = round2(TotalFeesWithDiscount + TotalResourceFees);
    const familyPayableFromCampuses = round2(
        campusBreakdown.reduce((sum, row) => sum + Number(row.payableAmount || 0), 0)
    );
    const MinimumAmount = round2(
        campusBreakdown.reduce((sum, row) => sum + Number(row.minimumAmount || 0), 0)
        || (TotalDepositFees + TotalResourceFees)
    );
    const TotalOutstandingAmount = round2(
        campusBreakdown.reduce((sum, row) => sum + (Number(row.outstandingAmount) || 0), 0)
    );
    const PayableAmount = round2(yearTotal + TotalOutstandingAmount);
    const requested = body.customAmount == null || body.customAmount === ''
        ? (usePerCampusPayment ? familyPayableFromCampuses : (familyDiscountOption === DISCOUNT_EBD || isStaff ? yearTotal : MinimumAmount))
        : Number(body.customAmount);
    const CustomAmount = isMultiCampus
        ? familyPayableFromCampuses
        : round2(Math.min(Math.max(requested, MinimumAmount), PayableAmount));

    const AICCreditAmount = round2(Math.min(creditAvailable, CustomAmount));
    const BPointAmount = round2(CustomAmount - AICCreditAmount);
    const BalanceDue = round2(Math.max(yearTotal - CustomAmount, 0));

    if (campusBreakdown.length > 0 && creditAvailable > 0) {
        campusBreakdown[0].credit_available = creditAvailable;
        campusBreakdown[0].credit_applied = Math.min(AICCreditAmount, campusBreakdown[0].payableAmount);
        campusBreakdown[0].AmountPaidFromAICCredit = campusBreakdown[0].credit_applied;
        campusBreakdown[0].AmountPaidFromBpoint = round2(
            Math.max(campusBreakdown[0].payableAmount - campusBreakdown[0].credit_applied, 0)
        );
        campusBreakdown[0].CustomAmount = campusBreakdown[0].payableAmount;
    }

    const paidSessionIds = new Set(
        getCampusPaymentCharges()
            .filter((row) => row.paymentStatus === 'paid')
            .map((row) => String(row.SessionID))
    );

    campusBreakdown.forEach((row) => {
        if (paidSessionIds.has(String(row.SessionID))) {
            row.paymentStatus = 'paid';
            row.alreadyPaidAmount = row.CustomAmount;
            // What that payment spent of this campus's credit. Reported rather than counted as
            // still available, so a family back for a second child is not shown the credit their
            // first payment used as though they still had it.
            row.creditAlreadyUsed = Number(row.credit_applied) || 0;
            row.credit_available = 0;
        }
    });

    const unpaidCampuses = campusBreakdown.filter((row) => row.paymentStatus !== 'paid');
    const AlreadyPaidAmount = round2(
        campusBreakdown
            .filter((row) => row.paymentStatus === 'paid')
            .reduce((sum, row) => sum + row.CustomAmount, 0)
    );
    const partialPayment = AlreadyPaidAmount > 0 && unpaidCampuses.length > 0;
    const allCampusesPaid = campusBreakdown.length > 0 && unpaidCampuses.length === 0;

    const RemainingBPointAmount = partialPayment || allCampusesPaid
        ? round2(unpaidCampuses.reduce((sum, row) => sum + row.AmountPaidFromBpoint, 0))
        : BPointAmount;
    const RemainingCreditAmount = partialPayment || allCampusesPaid
        ? round2(unpaidCampuses.reduce((sum, row) => sum + (row.credit_applied || 0), 0))
        : AICCreditAmount;
    const RemainingCustomAmount = partialPayment || allCampusesPaid
        ? round2(RemainingBPointAmount + RemainingCreditAmount)
        : CustomAmount;

    return {
        totals: {
            TotalSchoolFees,
            TotalDiscount,
            TotalSiblingDiscount: round2(students.reduce((sum, s) => sum + s.sibling_discount, 0)),
            TotalFeesWithDiscount,
            TotalResourceFees,
            TotalDepositFees,
            TotalAdelaideResourceOnlyDiscount: 0,
            TotalOutstandingAmount,
            PayableAmount,
            MinimumAmount,
            familyChildCount,
        },
        payment: {
            showTextField: !isMultiCampus && familyDiscountOption !== DISCOUNT_EBD && !isStaff,
            CustomAmount: RemainingCustomAmount,
            AICCreditAmount: RemainingCreditAmount,
            BPointAmount: RemainingBPointAmount,
            BalanceDue,
            // Only what is still spendable: a campus already paid has used its credit.
            credit_amount: round2(
                campusBreakdown
                    .filter((row) => row.paymentStatus !== 'paid')
                    .reduce((sum, row) => sum + (Number(row.credit_available) || 0), 0)
            ) || (unpaidCampuses.length === campusBreakdown.length ? creditAvailable : 0),
            payWithCard: RemainingBPointAmount > 0,
            payWithCreditOnly: RemainingBPointAmount <= 0 && RemainingCreditAmount > 0,
            AlreadyPaidAmount,
            creditAlreadyUsed: round2(
                campusBreakdown
                    .filter((row) => row.paymentStatus === 'paid')
                    .reduce((sum, row) => sum + (Number(row.creditAlreadyUsed) || 0), 0)
            ),
            partialPayment,
            allCampusesPaid,
        },
        validation: { valid: true, errorMessage: null },
        students,
        schoolFeeRows: buildSchoolFeeRows(),
        campusBreakdown,
    };
};

export const getMockResponse = (url, method, body = {}) => {
    const cleanUrl = url.replace(/^[a-zA-Z]+:\/\/[^/]+/, '').split('?')[0];

    switch (cleanUrl) {
        case '/parent-login': {
            const familyState = demoFamilyPaymentState();
            return {
                is_enabled: true,
                LoginToken: 'DEMO_JWT_TOKEN_123456789',
                data_log_id: 'DEMO_LOG_998877',
                SessionID: 'DEMO_SESSION_998877',
                PasswordMatched: 'Yes',
                Outstanding: 'No',
                OutstandingAmount: 0,
                RenrolStatus: familyState.allComplete ? 'Completed' : '',
                // Negative = campus credit; Login.js stores Math.abs() of this.
                credit_amount: -150,
                college: 'Kewdale',
                ...familyState,
            };
        }

        case '/checkStatus': {
            const familyState = demoFamilyPaymentState();
            return {
                t_date: new Date().toISOString().slice(0, 10),
                t_time: '10:00:00',
                is_reenrolment_open: true,
                RenrolStatus: familyState.allComplete ? 'Completed' : '',
                ...familyState,
            };
        }

        case '/parent/get-parent-history':
            return { parent: demoParent };

        case '/getPersonalandMedDetails':
            return {
                PerAddDet: [
                    {
                        mobile1: demoContact.mobile1,
                        mobile2: demoContact.mobile2,
                        e_mail: demoContact.email1,
                        e_mail2: demoContact.email2,
                        addr1: demoAddress.address_line1,
                        addr2: demoAddress.address_line2,
                        addr3: demoAddress.address_line3,
                        town_sub: demoAddress.town_suburb,
                        post_code: demoAddress.postcode,
                        state_code: demoAddress.state,
                    },
                ],
                childMedicalResult: demoParent.parent_codes.flatMap((c) =>
                    c.students.map((s) => ({
                        stud_code: s.student_code,
                        given_name: s.student_name,
                        surname: s.student_surname || demoParent.surname,
                        year_grp: s.year,
                        cmpy_code: s.campus_code,
                        campus_name: s.campus_name,
                        ...s.student_medical_data,
                    }))
                ),
            };

        case '/fetchEarlyBirdDate':
            return [{ date: `${NEXT_YEAR}-12-31T23:59:59.000Z` }];

        case '/fetchReenrolmentExpiryDate':
            return { expiry_date: `${NEXT_YEAR}-12-31T23:59:59.000Z` };

        case '/parent/fetchMultiCollegeDetails':
        case '/multiplecollegedata':
            return [];

        case '/parent/is-parent-staff':
            return { is_staff: false };

        case '/getschoolfees':
            return { result: buildSchoolFeeRows() };

        case '/calculatefees':
            return calculateFees(body);

        case '/parent/check-code-of-conduct':
            return { codeOfConductSubmitted: false };

        case '/parent/accept-code-of-conduct':
            return { success: true, status: 200, message: 'Code of conduct accepted successfully' };

        case '/getauthkey':
        case '/getauthkey-v2': {
            const calc = calculateFees(body);
            const unpaidCardCampuses = calc.campusBreakdown.filter(
                (row) => row.paymentStatus !== 'paid' && Number(row.AmountPaidFromBpoint) > 0
            );
            const chargeCampus = unpaidCardCampuses[0] || null;

            return {
                AuthKey: 'DEMO_BPOINT_AUTHKEY_998877',
                chargeCampus,
                unpaidCardCampuses,
                AmountPaidFromBpoint: calc.payment.BPointAmount,
                ChargeCampusAmount: chargeCampus
                    ? chargeCampus.AmountPaidFromBpoint
                    : calc.payment.BPointAmount,
                AmountPaidFromAICCredit: calc.payment.AICCreditAmount,
                CustomAmount: calc.payment.CustomAmount,
                RemainingAmount: calc.payment.BalanceDue,
                PayableAmount: calc.totals.PayableAmount,
                TotalResAmount: calc.totals.TotalResourceFees,
                TotalDepFees: calc.totals.TotalDepositFees,
                TotalSchoolFeeswodisc: calc.totals.TotalSchoolFees,
                TotalDiscount: calc.totals.TotalDiscount,
                TotalFeesWithDiscount: calc.totals.TotalFeesWithDiscount,
                calculation: { campusBreakdown: calc.campusBreakdown },
            };
        }

        case '/getTransactionResult':
            return {
                response: {
                    APIResponse: { ResponseCode: 0 },
                    TxnResp: {
                        BankResponseCode: '00',
                        ResponseText: 'Approved',
                        BillerCode: '2035954',
                        ReceiptNumber: 'DEMO-REC-998877',
                        MerchantNumber: '999888777',
                        ProcessedDateTime: new Date().toISOString(),
                    },
                },
            };

        case '/getDateTime': {
            const now = new Date();
            const dd = String(now.getDate()).padStart(2, '0');
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            return {
                datePart: `${dd}-${mm}-${now.getFullYear()}`,
                timePart: now.toTimeString().slice(0, 8),
            };
        }

        // Demo mode starts fresh every time, so there is nothing previously saved to put back.
        case '/fetchSavedParentDetails':
            return { ParentContact: null };

        case '/updatePersonalandMedDetails':
        case '/savePersonalandMedDetails':
            return {
                message: 'Details updated successfully',
                parent_record_status_changed: body?.parent_record_status_changed ?? true,
                parent_record_status: body?.parent_record_status || 'pending',
                enrolledchildrens: (body?.updatestud_detailsarr || []).filter(
                    (s) => s.enrollStatus === 'Enrolled'
                ),
            };

        case '/generateFeereceipt':
            return { status: 200, receiptUrl: '#' };

        default:
            return { status: 200, success: true, message: 'Success', data: body };
    }
};

export const demoAdapter = (config) => {
    const url = config.url || '';
    const method = (config.method || 'get').toLowerCase();

    let body = {};
    try {
        body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data || {};
    } catch (e) {
        body = config.data || {};
    }

    return new Promise((resolve) => {
        setTimeout(() => {
            resolve({
                data: getMockResponse(url, method, body),
                status: 200,
                statusText: 'OK',
                headers: { 'content-type': 'application/json' },
                config,
                request: {},
            });
        }, 120);
    });
};

export default demoAdapter;
